import { useMemo } from 'react';
import IdentityFormatter from './format/IdentityFormatter';
import CurrencyFormatter from './format/CurrencyFormatter';
import DateFormatter from './format/DateFormatter';
import IntegerFormatter from './format/IntegerFormatter';
import LettersOnlyFormatter from './format/LettersOnlyFormatter';
import NoSpecialCharFormatter from './format/NoSpecialCharFormatter';
import DigitFormatter from './format/DigitFormatter';
import NumbersOnlyFormatter from './format/NumbersOnlyFormatter';
import DecimalFormatter from './format/DecimalFormatter';
import { THOUSANDS_REGEX } from '../constants';

const FORMATS = [
  CurrencyFormatter,
  DateFormatter,
  IntegerFormatter,
  LettersOnlyFormatter,
  NumbersOnlyFormatter,
  NoSpecialCharFormatter,
  DigitFormatter,
  DecimalFormatter,
];

// All format values recognized by the form engine, in display order.
// Used by the inspector's Format dropdown so the options stay in sync
// with what the formatters actually support.
export const FORMAT_FIELD_OPTIONS = [
  // Widget / input-type specifiers (not formatters)
  { value: 'textarea', label: 'Text Area' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  // Formatter-based formats (from src/util/format/)
  { value: 'currency-$', label: 'Currency ($)' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'decimal-nonnegative', label: 'Decimal (non-negative)' },
  { value: 'integer', label: 'Integer' },
  { value: 'integer-nonnegative', label: 'Integer (non-negative)' },
  { value: 'letters-only', label: 'Letters Only' },
  { value: 'numbers-only', label: 'Numbers Only' }, // legacy alias of integer-nonnegative
  { value: 'no-special-char', label: 'No Special Characters' },
  { value: 'digit', label: 'Digit' },
];

export const IDENTITY_FORMATTER = new IdentityFormatter();

function applyFormatting(value, format) {
  return getFormatterFor(format).apply(value);
}

export function useAppliedFormatter(value, format, locale = 'en', isNumberField) {
  const formatter = useFormatter(format);

  return useMemo(() => {
    if (!format && isNumberField) return formatNumberWithCommas(value);

    return formatter.apply(value, locale);
  }, [value, formatter, locale, format]);
}

function formatNumberWithCommas(value) {
  if (value === undefined || value === null || value === '') return value;

  const n = Number(value);
  if (isNaN(n)) return undefined;

  const sign = n < 0 ? '-' : '';
  const [intPart, decPart] = Math.abs(n)
    .toFixed(2)
    .split('.');
  const trimmedDecPart = decPart.replace(/0+$/, '');

  return sign + intPart.replace(THOUSANDS_REGEX, ',') + (trimmedDecPart ? `.${trimmedDecPart}` : '');
}

export function useFormatter(format) {
  return useMemo(() => getFormatterFor(format), [format]);
}

function getFormatterFor(format) {
  return (
    (format
      ? typeof format === 'string'
        ? FORMATS.reduce((_, fmt) => {
            if (!_) return fmt.tryParse(format);

            return _;
          }, null)
        : format
      : null) || IDENTITY_FORMATTER
  );
}

export default applyFormatting;
